import { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Form, Input } from "antd";

const mapContainerStyle = {
  width: "100%",
  height: "320px",
};

const defaultCenter = { lat: 20.5937, lng: 78.9629 };

export default function LocationPicker({ form, partner, onLocationChange }) {
  const [marker, setMarker] = useState(null);

  // Watch latitude/longitude from the parent form so that
  // typing/searching in the Address field can move the map.
  const watchedLat = Form.useWatch("latitude", form);
  const watchedLng = Form.useWatch("longitude", form);
  const watchedAddress = Form.useWatch("address", form);

  const geocodeTimer = useRef(null);

  const extractAddressComponent = (components, types) => {
    const match = components.find((c) => c.types.some((t) => types.includes(t)));
    return match ? match.long_name : "";
  };

  const handleMarkerChange = (lat, lng) => {
    setMarker({ lat, lng });

    if (!window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      { location: { lat: Number(lat), lng: Number(lng) } },
      (results, status) => {
        if (status === "OK" && results?.length > 0) {
          const components = results[0].address_components || [];
          const formattedAddress = results[0].formatted_address;

          const country = extractAddressComponent(components, ["country"]);
          const state = extractAddressComponent(components, ["administrative_area_level_1"]);
          const city = extractAddressComponent(components, ["locality", "administrative_area_level_2"]);

          form.setFieldsValue({
            address: formattedAddress,
            latitude: lat,
            longitude: lng,
            country,
            state,
            city,
          });

          if (onLocationChange) {
            onLocationChange({ address: formattedAddress, country, state, city, latitude: lat, longitude: lng });
          }
        }
      },
    );
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handleMarkerChange(pos.coords.latitude, pos.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: true },
    );
  };

  // Initialize marker for edit mode or current location.
  useEffect(() => {
    if (partner?.latitude && partner?.longitude) {
      const lat = Number(partner.latitude);
      const lng = Number(partner.longitude);
      setMarker({ lat, lng });
      form.setFieldsValue({
        latitude: lat,
        longitude: lng,
        address: partner.address,
      });
      return;
    }
    getCurrentLocation();
  }, [partner]);

  // Whenever latitude/longitude in the form change (for example,
  // via address Autocomplete in the left column), update marker
  // and recenter the map.
  useEffect(() => {
    if (watchedLat && watchedLng) {
      const lat = Number(watchedLat);
      const lng = Number(watchedLng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        setMarker({ lat, lng });
      }
    }
  }, [watchedLat, watchedLng]);

  // Whenever address text changes (user typed or selected suggestion),
  // geocode it to get coordinates and move the map.
  useEffect(() => {
    if (!watchedAddress || watchedAddress.length < 3) return;
    if (!window.google || !window.google.maps) return;

    if (geocodeTimer.current) {
      clearTimeout(geocodeTimer.current);
    }

    geocodeTimer.current = setTimeout(() => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: watchedAddress }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          setMarker({ lat, lng });
          form.setFieldsValue({
            latitude: lat,
            longitude: lng,
          });
        }
      });
    }, 500);

    return () => {
      if (geocodeTimer.current) {
        clearTimeout(geocodeTimer.current);
      }
    };
  }, [watchedAddress, form]);

  return (
    <>
      <div style={{ margin: "0 0 16px" }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={marker || defaultCenter}
          zoom={marker ? 15 : 5}
          onClick={(e) => handleMarkerChange(e.latLng.lat(), e.latLng.lng())}
        >
          {marker && (
            <Marker
              position={marker}
              draggable
              onDragEnd={(e) =>
                handleMarkerChange(e.latLng.lat(), e.latLng.lng())
              }
            />
          )}
        </GoogleMap>
      </div>

      <Form.Item name="latitude" hidden>
        <Input />
      </Form.Item>

      <Form.Item name="longitude" hidden>
        <Input />
      </Form.Item>
    </>
  );
}

